import { gql } from "lib/apolloClient";
import {
  unitFlowFragment,
  unitFlowFragmentWithoutDepth,
  unitFlowOutlineFragment,
  unitResourceFragment,
} from "./Fragments";
import { studentDetailsFragment } from "ClassRoom/modules/ClassRoomFragments";

// This query is only used for import from unit flow modal
export const getUnitFlowFeedQuery = gql`
  query getUnitFlow(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $orderBy: ORDER_BY_ENUM
    $filters: LearningCourseFlowFilter
    $after: String
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on LearningCourse {
        id
        title
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(
            first: 100
            after: $after
            orderBy: $orderBy
            orderByDirection: ASC
            filters: $filters
          ) {
            edges {
              node {
                ...unitFlowWithChildrenItem
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
              __typename
            }
            resourceBasedCount {
              totalCount
              fileCount
              folderCount
              __typename
            }
          }
        }
      }
      __typename
    }
  }
  ${unitFlowFragment.unitFlowWithChildren}
`;

export const getUnitFlowOutlineFeedQuery = gql`
  query getUnitFlow(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $filters: LearningCourseFlowFilter
  ) {
    node(id: $parentId, type: $parentType) {
      ... on Course {
        id
        title
        classFlowFeedWrapper {
          learningCourseFlowFeedOutline(filters: $filters) {
            id
            label
            depth
            ...unitFlowOutlineItem
          }
        }
      }
      ... on LearningCourse {
        id
        title
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeedOutline(filters: $filters) {
            id
            label
            depth
            ...unitFlowOutlineItem
          }
        }
      }
    }
  }
  ${unitFlowOutlineFragment.unitFlowOutlineItem}
`;

export const getEntityFlowResourceMultiNodeQuery = gql`
  query getOrganizatonResources(
    $ids: [ID!]
    $filters: LearningCourseFlowFilter
    $syncedBluePrintItemFilters: SyncedBluePrintItemFilter
  ) {
    multiNode(ids: $ids, type: LEARNING_COURSE_FLOW) {
      ... on LearningCourseFlow {
        ...unitFlowWithChildrenItem
      }
    }
  }
  ${unitFlowFragmentWithoutDepth.unitFlowWithChildren}
`;

export const getEntireUnitFlowFeedQuery = gql`
  query getEntireUnitFlowFeed(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $filters: LearningCourseFlowFilter
    $syncedBluePrintItemFilters: SyncedBluePrintItemFilter
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        title
        classFlowFeedWrapper {
          learningCourseFlowFeed(
            orderBy: DISPLAY_SEQUENCE
            filters: $filters
            orderByDirection: ASC
            first: 10000
          ) {
            edges {
              node {
                id
                label
                depth
                resourceType: itemType
                parent {
                  id
                }
                item {
                  id
                  ... on Assessment {
                    type
                    academicTerm {
                      id
                      label
                      lockingStatus
                    }
                    assessmentType {
                      id
                      value
                    }
                    lockingInfo {
                      isLocked
                    }
                  }
                }
                isRepeated
                repeatedResources
                lockingInfo {
                  isLocked
                }
                isSyncedFromBluePrintCourse(
                  filters: $syncedBluePrintItemFilters
                )

                isSyncedWithAllTeacherCourseV2(
                  filters: $syncedBluePrintItemFilters
                ) {
                  status
                  isConflictExists
                  courseSyncStatus {
                    course {
                      id
                      title
                    }
                    status
                  }
                }
                childrenV2: children(filters: $filters) {
                  id
                }
                ...childrenHierarchyInfo
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
              __typename
            }
            resourceBasedCount {
              totalCount
              __typename
            }
          }
        }
        __typename
      }
      ... on LearningCourse {
        id
        title
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(
            orderBy: DISPLAY_SEQUENCE
            filters: $filters
            orderByDirection: ASC
            first: 10000
          ) {
            edges {
              node {
                id
                label
                depth
                resourceType: itemType
                parent {
                  id
                }
                item {
                  id
                  ... on Assessment {
                    type
                    academicTerm {
                      id
                      label
                      lockingStatus
                    }
                    assessmentType {
                      id
                      value
                    }
                    lockingInfo {
                      isLocked
                    }
                  }
                }
                isRepeated
                repeatedResources
                lockingInfo {
                  isLocked
                }
                isSyncedFromBluePrintCourse(
                  filters: $syncedBluePrintItemFilters
                )

                isSyncedWithAllTeacherCourseV2(
                  filters: $syncedBluePrintItemFilters
                ) {
                  status
                  isConflictExists
                  courseSyncStatus {
                    course {
                      id
                      title
                    }
                    status
                  }
                }
                childrenV2: children(filters: $filters) {
                  id
                }
                ...childrenHierarchyInfo
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
              __typename
            }
            resourceBasedCount {
              totalCount
              __typename
            }
          }
        }
        __typename
      }
    }
  }
  ${unitFlowFragment.childrenHierarchyInfoFragment}
`;

export const getUnitResourcesFeedQuery = gql`
  query getUnitResourcesFeed(
    $parentId: ID!
    $parentType: ENTITY_TYPE_ENUM!
    $filters: LearningCourseFlowFilter
    $after: String
    $first: Int
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        title
        classFlowFeedWrapper {
          learningCourseFlowFeed(
            orderBy: DISPLAY_SEQUENCE
            filters: $filters
            orderByDirection: ASC
            after: $after
            first: $first
          ) {
            edges {
              node {
                ...unitResourceItem
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
              __typename
            }
            resourceBasedCount {
              totalCount
              __typename
            }
          }
        }
        __typename
      }
      ... on LearningCourse {
        id
        title
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(
            orderBy: DISPLAY_SEQUENCE
            filters: $filters
            orderByDirection: ASC
            after: $after
            first: $first
          ) {
            edges {
              node {
                ...unitResourceItem
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
              __typename
            }
            resourceBasedCount {
              totalCount
              __typename
            }
          }
        }
        __typename
      }
    }
  }
  ${unitResourceFragment}
`;

export const getUnitResourcesCountQuery = gql`
  query getUnitResourcesCount(
    $parentId: ID!
    $filters: LearningCourseFlowFilter
    $parentType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $parentId, type: $parentType) {
      id
      ... on Course {
        id
        classFlowFeedWrapper {
          learningCourseFlowFeed(filters: $filters) {
            resourceBasedCount {
              totalCount
              __typename
            }
          }
        }
        __typename
      }
      ... on LearningCourse {
        id
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(filters: $filters) {
            resourceBasedCount {
              totalCount
              __typename
            }
          }
        }
        __typename
      }
    }
  }
`;

export const getLearningCourseFlowResourceQuery = gql`
  query getLearningCourseFlowResource($id: ID!) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        learningCourseFlowResource {
          id
        }
      }
    }
  }
`;

export const getAssociatedClassesForSharingQuery = gql`
  query getAssociatedClassesForSharing(
    $learningCourseId: ID!
    $classFilters: LearningCourseClassFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $learningCourseId, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        classes(filters: $classFilters) {
          id
          title
          isActive
          isArchived
          profileImageData {
            icon
            color
            acronym
          }
          students {
            totalCount
            edges {
              node {
                ...studentItem
              }
            }
          }
        }
      }
    }
  }
  ${studentDetailsFragment.basicStudentDetails}
`;

export const getUnitFlowItemCountInUnitPlanQuery = gql`
  query getUnitPlan($id: ID!) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        id
        flowResourceCount {
          totalCount
        }
      }
    }
  }
`;
